import React, { useEffect, useState } from 'react';

const parseCSV = (csvText, isCommaSeparated = false) => {
  const lines = csvText.split('\n').filter(line => line.trim() !== '');
  if (lines.length === 0) return [];
  const header = lines[0].split(';').map(h => h.trim());

  return lines.slice(1).map((line, rowIndex) => {
    const parts = line.split(';').map(p => p.trim());
    if (parts.length !== header.length) {
      console.warn(
        `parseCSV warning: row #${rowIndex + 2} ha un numero di colonne diverso: previsto ${header.length} ma trovato ${parts.length}`,
        parts
      );
    }
    let obj = {};
    header.forEach((h, i) => {
      let val = parts[i];
      if (isCommaSeparated) {
        val = val.replace(',', '.');
      }
      const floatVal = parseFloat(val);
      if (h === 'Timestamp') {
        obj.timestamp = val;
      } else if (i === 3) {
        obj.energy = isNaN(floatVal) ? 0 : floatVal;
        obj.cost = obj.energy;
      } else if (h === 'CO2 Emissions (kg)') {
        obj.co2 = isNaN(floatVal) ? 0 : floatVal * 1000;
      } else if (h === 'T_zone') {
        obj.indoor = isNaN(floatVal) ? 0 : floatVal;
      }
    });
    return obj;
  });
};
const UserStats = ({
  results,
  totalCost = 0,
  totalEmissions = 0,
  totalEnergyConsumed = 0,
  points = 0,
  onScoreComputed
}) => {
  const [kpiConfig, setKpiConfig] = useState(null);

  useEffect(() => {
    // Carica in parallelo i tre file CSV
    Promise.all([
      fetch('/baseline.csv').then(res => res.text()),
      fetch('/MPC.csv').then(res => res.text()),
      fetch('/cph.csv').then(res => res.text())
    ])
      .then(([baselineText, mpcText, cphText]) => {
        const baselineData = parseCSV(baselineText.replace(/^\uFEFF/, ''), true);
        const mpcData = parseCSV(mpcText.replace(/^\uFEFF/, ''), true);
        const cphData = parseCSV(cphText.replace(/^\uFEFF/, false));

        // Seleziona le prime 120 righe (5 giorni) per baseline (emissioni ed energia) e MPC
        const baselineFirst120 = baselineData.slice(0, 120);
        const mpcFirst120 = mpcData.slice(0, 120);

        // Somma per baseline (emissioni ed energia) dal file baseline.csv
        const baselineSum = baselineFirst120.reduce((acc, row) => {
          acc.totalCost += (row.cost || 0);
          acc.totalCo2 += (row.co2 || 0);
          acc.totalEnergy += (row.energy || 0);
          return acc;
        }, { totalCost: 0, totalCo2: 0, totalEnergy: 0 });

        // Somma per l'ottimale (MPC) dal file MPC.csv
        const mpcSum = mpcFirst120.reduce((acc, row) => {
          acc.totalCost += (row.cost || 0);
          acc.totalCo2 += (row.co2 || 0);
          acc.totalEnergy += (row.energy || 0);
          return acc;
        }, { totalCost: 0, totalCo2: 0, totalEnergy: 0 });

        // Calcolo dell'optimal cost dal file cph.csv: prime 120 righe, 5a colonna (indice 4)
        const cphLines = cphText.replace(/^\uFEFF/, '').split('\n').filter(line => line.trim() !== '');
        const cphFirst120 = cphLines.slice(1, 121);
        
        const optimalCostFromCPH = cphFirst120.reduce((acc, line, rowIndex) => {
          const parts = line.split(';').map(p => p.trim());
          if (parts.length < 5) {
            console.warn(`cph CSV warning: row ${rowIndex + 2} ha meno di 5 colonne`);
            return acc;
          }
          // Accediamo alla 5a colonna (indice 4) per l'optimal cost
          let val = parts[4].replace('.', ',');
          const floatVal = parseFloat(val);
          return acc + (isNaN(floatVal) ? 0 : floatVal);
        }, 0);

        const baselineCosto =  15515
        // Configurazione KPI: usa optimalCostFromCPH per l'optimal cost
        const config = {
          optimalCost: optimalCostFromCPH,  // Assegna l'optimal cost dalla colonna 5
          baselineCost: baselineCosto,  // Baseline cost calcolato separatamente
          optimalEmissions: mpcSum.totalCo2,
          baselineEmissions: baselineSum.totalCo2,
          optimalEnergy: mpcSum.totalEnergy,
          baselineEnergy: baselineSum.totalEnergy,
          maxComfortPoints: 45
        };

        setKpiConfig(config);
      })
      .catch(err => console.error("Error loading KPI CSVs:", err));
  }, []);


  // Calcolo dei punteggi
  let costScore = 0, emissionScore = 0, energyScore = 0, comfortScore = 0, finalScore = 0;

  if (kpiConfig) {
    const {
      optimalCost,
      baselineCost,
      optimalEmissions,
      baselineEmissions,
      optimalEnergy,
      baselineEnergy,
      maxComfortPoints
    } = kpiConfig;

    const costDenom = (baselineCost - optimalCost) || 1;
    const emissionDenom = (baselineEmissions - optimalEmissions) || 1;
    const energyDenom = (baselineEnergy - optimalEnergy) || 1;

    costScore = 100 * (1 - (totalCost - optimalCost) / costDenom);
    emissionScore = 100 * (1 - (totalEmissions - optimalEmissions) / emissionDenom);
    energyScore = 100 * (1 - (totalEnergyConsumed - optimalEnergy) / energyDenom);
    comfortScore = 100 * (points / maxComfortPoints);

    costScore = Math.max(0, Math.min(100, costScore));
    emissionScore = Math.max(0, Math.min(100, emissionScore));
    energyScore = Math.max(0, Math.min(100, energyScore));
    comfortScore = Math.max(0, Math.min(100, comfortScore));

    finalScore = (costScore + emissionScore  + comfortScore) / 3;
  }

  // Calcolo del comfort basato sulla temperatura interna durante le ore di occupazione
  const calculateComfortScore = (indoorTemp) => {
    let deviation = 0;
    if (indoorTemp < 20) {
      deviation = 20 - indoorTemp;
    } else if (indoorTemp > 23) {
      deviation = indoorTemp - 23;
    }
    const score = 1 - deviation;
    return Math.max(score, -1);
  };

  let totalComfort = 0;
  let countComfort = 0;
  let occupancyHours = 0;
  let goodAirHours = 0;

  results.forEach((r) => {
    const hourOfDay = Math.floor((r.time * 24) % 24);
    if (hourOfDay >= 8 && hourOfDay < 17) {
      const comfort = calculateComfortScore(r.indoor);
      totalComfort += comfort;
      countComfort++;
      occupancyHours++;
      if (r.indoor >= 20 && r.indoor <= 23) {
        goodAirHours++;
      }
    }
  });

  const avgComfort = countComfort > 0 ? totalComfort / countComfort : 0;
  const S_IAQ = occupancyHours > 0 ? (goodAirHours / occupancyHours) * 100 : 0;

  useEffect(() => {
    if (onScoreComputed && kpiConfig) {
      onScoreComputed(finalScore);
    }
  }, [finalScore, onScoreComputed, kpiConfig]);

  return (
    <div className="user-stats">
      <h2>User Statistics</h2>
      {!kpiConfig && <p>Loading KPI configuration...</p>}

      {kpiConfig && (
        <>
          <section>
            <h3>Weighted Scores</h3>
            <p><strong>Cost Score:</strong> {costScore.toFixed(2)}</p>
            <p><strong>Emission Score:</strong> {emissionScore.toFixed(2)}</p>
            <p><strong>Comfort Score:</strong> {comfortScore.toFixed(2)}</p>
            <p><strong>Final Score:</strong> {finalScore.toFixed(2)}</p>
            
          </section>

          <section>
            <h3>Additional Metrics</h3>
            <p><strong>Average Comfort (Simulation):</strong> {avgComfort.toFixed(2)}</p>
            <p><strong>Air Quality (IAQ):</strong> {S_IAQ.toFixed(2)}%</p>
          </section>

          <section>
            <h3>Raw Totals</h3>
            <p><strong>Simulated Total Cost:</strong> {totalCost.toFixed(2)}</p>
            <p><strong>Baseline Cost:</strong> {kpiConfig.baselineCost.toFixed(2)}</p>
            <p><strong>Optimal Cost:</strong> {kpiConfig.optimalCost.toFixed(2)}</p>

            <p><strong>Simulated Total Emissions:</strong> {totalEmissions.toFixed(2)}</p>
            <p><strong>Baseline Emissions:</strong> {kpiConfig.baselineEmissions.toFixed(2)}</p>
            <p><strong>Optimal Emissions:</strong> {kpiConfig.optimalEmissions.toFixed(2)}</p>

            <p><strong>Simulated Total Energy:</strong> {totalEnergyConsumed.toFixed(2)}</p>

            <p><strong>Comfort Points:</strong> {points}</p>
          </section>
        </>
      )}
    </div>
  );
};

export default UserStats;
